import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import template from './record-list-widget.hbs'
import RecordList from 'record-list'
import arrayToHash from 'scoreboard/components/array-to-hash'
import FieldValueControl from 'scoreboard/datasets/records/field-value-control'
import FieldValueFormatter from 'scoreboard/datasets/records/field-value-formatter'
import FormView from 'scoreboard/components/form-view'
import rowActionTemplate from './row-action-template.hbs'
import disableControls from 'scoreboard/components/disable-controls'
import Finder from 'scoreboard/components/finder'
import escapeStringRegexp from 'escape-string-regexp'
import formHelper from 'scoreboard/components/form-helper'
import getRecordListFieldIds from './get-record-list-field-ids'
import RecordUtil from 'scoreboard/datasets/records/record-util'
const { isSupplementalOnly } = RecordUtil
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import Tooltip from 'scoreboard/components/tooltip'
import i18n from 'scoreboard/components/i18n'
import { recordInsertionNotAllowedMessage } from 'scoreboard/forms/actions/save-record-actor'
import createDatasetRecord from 'scoreboard/datasets/model/dataset-record'
import isGeographicOrRollup from '../../datasets/is-geographic-or-rollup'
import FormActionType from 'scoreboard/forms/model/form-action-type'
const { GO_TO_PAGE } = FormActionType
import DataType from 'scoreboard/datasets/model/data-type'
const { TEXT, ROLLUP, CLUSTER } = DataType
import { isFieldHiddenForUser } from 'scoreboard/forms/model/form'
import EvaluateStatementsActor from '../actions/evaluate-statements-actor'
import BusyButton from '../../components/busy-button'

const RecordListWidget = Backbone.View.extend({

	tagName: 'li',

	className: 'record-list-widget',

	events: {
		'click .save': 'submit', // because we're not using a html form
		'click .not-editable-pk-value': 'onClickNotEditablePkValue'
	},

	/**
	 * The options passed into the RecordListWidget.
	 *
	 * @typedef {Object} RecordListWidgetOptions
	 * @property {Form} model
	 * @property {FormView} formView the "parent" formView we'll use for this
	 * components formView options.
	 * @property {GuardOptions} formView.options.guardOptions
	 * @property {FormWidget} widget
	 * @property {FormDirector} [formDirector] the form director. Only required
	 * on the Overview tab
	 * @property {Boolean} [allowInput=true] indicates whether the widget
	 * is allowing data to be entered
	 */

	/**
	 * The record list widget
	 *
	 * @constructs
	 * @property {Boolean} allowInput indicates whether the widget
	 * is allowing data to be entered
	 * @property {FormView} formView
	 * @property {Form} model
	 * @property {RecordListWidgetOptions} options
	 * @property {Object<Number,Boolean[]>} editedById the "edited" arrays
	 * keyed by ID. These are maintained only for those rows which actually
	 * changed
	 * @property {Object<Number,Number>} columnIndexesByFieldId field ID to index in
	 * recordListResponse.records
	 * @property {Object<Number,RecordListItem>} recordListItemsById
	 * @property {Number[]} recordListFieldIds see get-record-list-field-ids.js
	 * @property {Object<Number, FieldValueControl[]>} fieldValueControlsById all the
	 * field value controls that have been instantiated, keyed by node ID
	 *
	 * @param {RecordListWidgetOptions} options
	 */
	initialize: function(options = {}) {
		this.options = options
		this.fieldValueControlsById = {}
		this.editedById = {}
		this.initializeTreeItemsById()
		// must be after initializeTreeItemsById
		this.fieldValueFormatter = new FieldValueFormatter(this.model.dataset,
			this.treeItemsById)
		this.allowInput = _.get(options, 'allowInput', true)

		this.recordListFieldIds = getRecordListFieldIds(
			this.options.widget.columns, this.model.dataset)
		// #35537 remove field id's for hidden fields
		this.recordListFieldIds = _.filter(this.recordListFieldIds, fieldId =>
			!isFieldHiddenForUser(fieldId, this.model.dataset))
		this.initializeColumnIndexes()

		// if the dataset has no primary key, it will have a GENERATED_KEY
		// in the record data at the index after the visible fields
		const pkId = this.model.dataset.get('primaryKeyFieldId')
		this.pkIndex = _.isNil(pkId) ? this.recordListFieldIds.length :
			this.columnIndexesByFieldId[pkId]

		// must be after pkIndex is set
		this.initializeRecordListItemsById()

		this.nodes = this.getNodes()
		this.originalNodes = _.cloneDeep(this.nodes)

		const {widget, formView} = this.options
		// We must use a different formView for each record list, but we want
		// to apply the forms-overview guard options for consistency
		const guardOptions = _.get(formView, 'options.guardOptions')

		this.formView = new FormView({
			el: this.el,
			id: `widget_${widget.id}`,
			guardOptions,
			toggleCancel: true,
			submit: this.submit.bind(this)
		})
		this.formView.on('cancel', e => this.onClickCancel())
	},

	initializeColumnIndexes: function() {
		this.columnIndexesByFieldId = {}
		let lastColumnIndex = 0
		let columnFieldIds = arrayToHash(
			_.map(this.options.widget.columns, 'fieldId'))
		_.each(this.model.dataset.get('fields'), field => {
			// #36097 initialize column index for all columns, so the
			// pkIndex will be accurate if its position is after a hidden column
			if ((columnFieldIds[field.id] || field.primaryKey)) {
				this.columnIndexesByFieldId[field.id] = lastColumnIndex++
			}
		})
	},

	initializeRecordListItemsById: function() {
		this.recordListItemsById = {}
		let response = this.options.widget.recordListResponse
		_.each(response.edited, (edited, index) => {
			const recordId = response.records[index][this.pkIndex]
			const generatedKey = _.last(response.records[index])
			this.recordListItemsById[recordId] = {
				edited,
				record: response.records[index],
				new: response.new[index],
				editable: response.editable[index],
				generatedKey
			}
		})
	},

	initializeTreeItemsById: function() {
		let treeItems = _(this.model.get('widgets'))
			.map(w => _.get(w, 'recordListResponse.treeItems'))
			.flatten()
			.value()
		treeItems = _.concat(treeItems, this.model.get('tree'))
		this.treeItemsById = _.keyBy(treeItems, 'id')
	},

	render: function() {
		const {widget} = this.options
		this.$el.html(template(widget))
		this.renderRecordList()
		this.renderFinder()
		let hasEditableColumn = _.find(widget.columns, column =>
			column.editable)
		this.$('.save, .cancel').toggleClass(
			'is-not-displayed', !hasEditableColumn)
		if (hasEditableColumn && this.formView) {
			this.formView.start()
		}
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		this.renderBanner()

		return this
	},

	renderRecordList: function() {
		let editableColumns = _(this.options.widget.columns)
			.map((column, index) => {
				if (column.editable) {
					return index
				}
				return null
			})
			// Can't use .compact() because 0 index is possible
			.filter(val => val !== null)
			.value()

		_.invoke(this.recordList, 'remove')

		let columns = this.getColumns()
		let nodes = this.nodes
		let firstColumn = _.head(columns)
		// #35316 Sort records in record list widget by leftmost column
		// #37817 do not attempt sorting if the leftmost column is a rollup column
		// and sort the nodes before they are sent into the record-list
		if (firstColumn && !isGeographicOrRollup(firstColumn.type)) {
			nodes = _.sortBy(nodes, 'columns[0]')
		}
		this.recordList = new RecordList(this.$('.container')[0], nodes, {
			editableColumns,
			readFromDom: el => {
				let value = _.first(_.values(formHelper.readFromDom(el)))
				// for numeric fields preserve invalid input
				if (_.isNaN(value)) {
					// I tried to preserve the invalid input, but it was too
					// much work and I'm pressed for time
					value = ''
				}
				return value
			},
			rowAction: this.buildRowAction(),
			columns
		})

		this.recordList.render()
		this.recordList.on('node-removed', node =>
			_.invokeMap(this.fieldValueControlsById[node.id], 'remove'))
		this.recordList.tree.on('change:height', () => {
			let controls = _(this.fieldValueControlsById)
				.values()
				.flatten()
				.value()
			// this is clumsy and hides the tooltips way more than I want, but
			// there's not enough time to make this better right now
			_.invokeMap(controls, 'markedField.removeTooltip')
		})
	},

	renderFinder: function() {
		_.invoke(this.finder, 'remove')
		this.finder = new Finder()
		this.$('.finder').append(this.finder.el)
		this.finder.render()
		// #52804 keep previous search text
		if (this.prevSearch != null) {
			this.finder.text(this.prevSearch)
		}
		this.finder.on('find', text => {
			// if all the finder text was cleared, re-render the record-list
			// to it's original state to ensure the column calculations
			// are accurate #60337
			if (_.isEmpty(text)) {
				this.render()
				// Keep the finder focused because the user never clicked
				// outside of it.
				this.finder.$el.find('input').focus()
			} else {
				this.recordList.search(text, this.recordListSearchFilter.bind(this))
			}
		})
	},

	/**
	 * The filter function passed to the recordList's search feature
	 */
	recordListSearchFilter: function(text, node) {
		let columnText = _.map(this.options.widget.columns, (column, index) => {
			let field = this.model.dataset.combinedFieldsById[column.fieldId]

			// handle null values for text data types, calling
			// fieldValueFormatter.format will overescape the value
			return field.dataType === TEXT ?
				_.toString(node.columns[index]) :
				this.fieldValueFormatter.format(field, node.columns[index])
		})
		let regExp = new RegExp(escapeStringRegexp(text) || '', 'i')
		return !!_.find(columnText, colText => regExp.test(colText))
	},

	renderBanner: function() {
		const response = this.options.widget.recordListResponse
		if (_.isNil(response.queryError)) return

		this.$('.banner-error').text(i18n.get(response.queryError.messageCode))
			.removeClass('is-not-displayed')
	},

	buildRowAction() {
		if (_.isEmpty(this.options.widget.buttonLabel)) {
			return undefined
		}

		return (containerEl, node) => {
			containerEl = $(containerEl)
			containerEl.html(rowActionTemplate({
				label: this.options.widget.buttonLabel
			}))
			const recordListItem = this.recordListItemsById[node.id]
			if (!this.allowInput || _.isNil(recordListItem)) {
				disableControls(containerEl)
			}
			containerEl
				.find('button')
				.addClass('button button-default small')
				.on('click', (e) => this.onSelectRecord(e, node))
		}
	},

	/**
	 * Callback executed after a user selects a record. Loads the record and
	 * then proceeds with any additional actions attached to the rowAction
	 * button
	 */
	onSelectRecord: async function(e, node) {
		let params = this.options.formDirector.requestParamsObj()
		params['generatedKey'] = node.generatedKey
		let url =`api/datasets/${this.model.dataset.id}/records?${$.param(params)}`

		// Evaluates conditionals and returns appropriate actions
		const actor = new EvaluateStatementsActor({
			formDirector: this.options.formDirector,
			model: this.model,
			formView: this.formView,
			widget: this.options.widget
		})
		let statementGroups =
			this.options.widget?.statements ?? this.model?.statements
		this.busyButton = new BusyButton({el: this.$(e.target)}).loading()
		const { hasGoToPage } =
			this.options.formDirector.hasActions(await actor.act())

		// If the record is properly set, we can proceed with the actions
		await $.get({
			url,
			success: record => this.options.formDirector.setRecord(record)
		}).then(async () => {

			// #59158
			// Execute each statement group in isolation so that the next group has
			// the context of the previous.
			let i = 0
			do {
				const statements = statementGroups[i]
				const actorActions = await actor.act(
					_.size(statementGroups) == 0 ? statementGroups : [statements]
				)
				const actions = actorActions.map(action => {
					// Indicate that this is from a record list for go-to-page actions
					if (action.type === GO_TO_PAGE) {
						return { ...action, recordListOrigin: true }
					}
					return action
				})

				await this.options.formDirector.direct(
					actions,
					{
						busyButton: this.busyButton,
						toggleBusyIfNoActions: true,
						hasGoToPage
					})
				i++
			} while (i < statementGroups.length)
		}).catch(() => {
			// When record retrieval fails, ensure the loading animation is stopped
			this.busyButton?.enable()
		})
	},

	/**
	 * Builds the nodes to pass to the RecordList
	 */
	getNodes: function() {
		let firstFieldId = this.options.widget.columns[0].fieldId
		let firstField = this.model.dataset.combinedFieldsById[firstFieldId]
		if (firstField.primaryKey && isGeographicOrRollup(firstField.dataType)) {
			return this.getNodeTree()
		} else {
			return this.getNodeList()
		}
	},

	/**
	 * Builds the node list to pass to RecordList for record lists which are
	 * simple lists instead of trees
	 */
	getNodeList: function() {
		return _.map(this.options.widget.recordListResponse.records, record => {
			let id = record[this.pkIndex]

			// #36097 use this.getColumns() for only non-hidden columns
			// we don't want to use _.compact here, since it removes all falsy values
			let columns = _.map(this.getColumns(), column => {
				let index = this.columnIndexesByFieldId[column.fieldId]
				return record[index]
			})
			// generated_key is always last in recordListResponse
			const generatedKey = _.last(record)
			return { id, columns, generatedKey }
		})
	},

	/**
	 * Builds the node list to pass to RecordList for record lists which are
	 * trees instead of simple lists
	 */
	getNodeTree: function() {
		let nodeList = this.getNodeList()
		let nodeListById = _.keyBy(nodeList, 'id')
		let nodes = _.map(this.model.get('tree'), treeItem => {
			let node = nodeListById[treeItem.id]
			if (node) {
				node.parentId = treeItem.parentId
			} else {
				// need empty strings or else `renderField` isn't called by
				// by the record-list correctly
				let columns = _.map(this.options.widget.columns, () => '')
				columns[0] = treeItem.id
				node = _.extend({ columns },
					_.pick(treeItem, 'id', 'parentId'))
			}
			return node
		})
		nodes[0].collapsed = false
		return nodes
	},

	/**
	 * Builds the columns to pass to the RecordList. The data returned here
	 * describes how to render each of the columns in the record list.
	 */
	getColumns: function() {
		return _.compact(_.map(this.options.widget.columns, column => {
			let field = this.model.dataset.combinedFieldsById[column.fieldId]
			if (isFieldHiddenForUser(field.id, this.model.dataset)) {
				return null
			}
			const type = isGeographicOrRollup(field.dataType) ?
				ROLLUP : field.dataType
			return {
				label: _.escape(field.label), // #59448 don't forget to escape!
				type,
				fieldId: field.id, // #36097 fieldId needed for this.getNodeList()
				render: (el, value, node, index) =>
					this.renderField(el, column, field, value, node, index)
			}
		}))
	},

	/**
	 * Renders a field, which may be an editable control or just some text
	 * showing the value
	 * @param {FormWidgetColumn} column
	 * @param {Object} field the field in the dataset
	 * @param {Object} value the default value for the field
	 * @param {Object} node the node in the record list
	 * @param {Number} index the index within the column list where the change
	 * was made
	 */
	renderField: function(el, column, field,
			value, node, index) {
		// #35221 in addition to checking if the widget column is marked
		// "editable", also check to see if the dataset field can be editable
		if (this.recordFieldEditable(field, node) && column.editable) {
			return this.renderFieldValueControl(el, column, field, value, node)
		} else {
			// #42568 revert part of #36133 to prevent over-escaping non-editable
			// text (since this.fieldValueFormatter.format calls
			// escapePreserveLineBreaks which escapes the text, noted in #39751)
			// If its not both editable in the record list and in the dataset,
			// only show its content and remove the editable styles
			$(el).html(this.fieldValueFormatter.format(field, value))
				.closest('.column').removeClass('column-editable')
			// don't need tooltips if this field isn't even editable and the widget doesn't
			// have the field as editable either
			if (!field.editable || !column.editable) {
				return
			}
			// #36958 if this is the PK, show a tooltip for an explanation of why it can't be edited
			// don't ever show the tooltip for manually added fields
			if (this.isPrimaryKey(field) && !this.isManuallyAdded(node)) {
				$(el).addClass('not-editable-pk-value')
			}
		}
	},

	onClickNotEditablePkValue: function(e) {
		_.remove(this.notEditablePkTooltip, 'remove')
		this.notEditablePkTooltip = new Tooltip({
			attachTo: e.target,
			content: i18n.get('datasets.primaryKey.notEditable.manuallyAddedOnly')
		}).render()
	},

	// #35221 determines if a record field should be editable based on
	// if the dataset field is set to be editable, or if this is a
	// new (manually added) record
	recordFieldEditable(field, node) {
		if (this.isPrimaryKey(field)) {
			return field.editable && this.isManuallyAdded(node)
		}

		return field.dataType !== CLUSTER &&
			(field.editable || this.isManuallyAdded(node))
	},

	isManuallyAdded: function(node) {
		const recordListItem = this.recordListItemsById[node.id]
		return isSupplementalOnly(recordListItem)
	},

	isPrimaryKey: function(field = {}) {
		const dataset = this.model.get('dataset') || {}
		return dataset.primaryKeyFieldId === field.id
	},

	/**
	 * Renders a field value control
	 * @param {FormWidgetColumn} column
	 * @param {Object} field the field in the dataset
	 * @param {Object} value the default value for the field
	 * @param {Object} node the node in the record list
	 * was made
	 */
	renderFieldValueControl: function(el, column, field,
			value, node) {
		let recordListItem = this.recordListItemsById[node.id]

		let control = new FieldValueControl({
			fieldId: column.fieldId,
			model: this.model.dataset,
			suggestions: this.model.get('suggestions'),
			value,
			// there isn't enough room in the UI for these extra controls
			enableEditUndo: false,
			formView: this.formView,
			treeItemsById: this.treeItemsById,
			enableLazySelect: true
		})
		$(el).html(control.el)
		control.render()
		control.on('change', () => {
			this.recordList.change(node.id)
			let edited
			if (recordListItem) {
				edited = recordListItem.edited
			} else {
				// if no row was sent from the server, make one. This can
				// be the case only if the primary key is a rollup and the
				// first column in the record list is the PK
				edited = _.fill(Array(this.recordListFieldIds.length), false)
			}

			const fieldIdIndex = _.indexOf(this.recordListFieldIds, field.id)
			edited[fieldIdIndex] = true
			this.editedById[node.id] = edited
		})

		if (!this.fieldValueControlsById[node.id]) {
			this.fieldValueControlsById[node.id] = []
		}
		this.fieldValueControlsById[node.id].push(control)
		if (!this.allowInput) {
			disableControls(control.el)
		}

		// #46414 if there is no record, it was filtered out and we want to
		// disable the controls without displaying a tooltip
		if (_.isNil(recordListItem)) {
			disableControls(control.el)
		} else {
			this.handleNoNewRecords(control, recordListItem)
		}

		return control.el
	},

	/**
	 * Handles disabling the controls for records that would have to be newly
	 * created if new records aren't allowed
	 * @param {FieldValueControl} control
	 * @param {RecordListItem} recordListItem the existing record being
	 * edited if there is one, or undefined otherwise
	 */
	handleNoNewRecords: function(control, recordListItem) {
		let dataset = this.model.get('dataset')
		if (dataset.newAllowed || recordListItem) return

		disableControls(control.el)

		control.$el.on('click', () => {
			_.invoke(this.noNewRecordsTooltip, 'remove')
			this.noNewRecordsTooltip = new Tooltip({
				attachTo: control.el,
				content: recordInsertionNotAllowedMessage(dataset.label, dataset.id)
			}).render()
		})
	},

	onClickCancel: function() {
		this.prevSearch = this.$('.input-rounded.find-input').val()
		this.removeComponents()
		this.nodes = _.cloneDeep(this.originalNodes)
		this.render()
	},

	submit: function() {

		if (this.formView.primaryButton.state === 'disabled') return

		// the default test function returns any marked fields with errors, and
		// if there are any, don't allow the submit
		if (this.formView.test()) {
			this.formView.primaryButton.disable()
			return
		}
		this.formView.primaryButton.loading()
		const nodesById = _.keyBy(this.nodes, 'id')
		let removeGeneratedKey = _.some(this.editedById, (p, id) => {
			const node = nodesById[id]
			const recordItem = this.recordListItemsById[id]
			return _.get(recordItem, 'record').length > node.columns.length
		})
		let records = _.map(this.editedById, (editedByRecordOrder, id) => {
			const node = nodesById[id]
			let recordListItem = this.recordListItemsById[id]
			const originalRecord = _.get(recordListItem, 'record')

			let record, edited
			// editing an existing item
			if (recordListItem) {
				record = _.clone(recordListItem.record)
				edited = _.clone(recordListItem.edited)
				// remove the generated_key values from the record before sending to the API
				if (removeGeneratedKey) {
					record?.pop()
					edited?.pop()
				}
			} else {
				// could be creating a new record in a record list with a rollup
				// primary key (#34869)
				let size = _.size(this.columnIndexesByFieldId)
				record = Array(size)
				edited = Array(size)
			}
			// change the edited indicators to true for items that were edited
			_.each(this.options.widget.columns, ({ fieldId }, widgetColumnIndex) => {
				// let columnIndex = this.columnIndexesByFieldId[columnMetadata.fieldId]
				const fieldIndexInRecord = _.indexOf(this.recordListFieldIds, fieldId)
				record[fieldIndexInRecord] = node.columns[widgetColumnIndex]

				const valueEditedInRecordList = !!editedByRecordOrder[fieldIndexInRecord]
				// do not mess with any "edited" values for items that weren't edited
				// in the UI as this will change their value in the database
				if (valueEditedInRecordList) {
					edited[fieldIndexInRecord] = valueEditedInRecordList
				}
			})
			return createDatasetRecord({
				record,
				originalRecord,
				edited,
				new: isSupplementalOnly(recordListItem),
				insert: _.isEmpty(_.get(recordListItem, 'record')),
				generatedKey: recordListItem.generatedKey
			})
		})
		console.dir(records)

		// requestParams takes care of adding in an embedded token if present
		const params = this.options.formDirector.requestParams()
		let xhr = $.post({
			url: `api/datasets/${this.model.dataset.id}/records${params}`,
			data: JSON.stringify({ records, fieldIds: this.recordListFieldIds }),
			success: () => {
				this.updateRecordListItemsById(records, removeGeneratedKey)
				this.formView.start()
				this.originalNodes = this.nodes
				// we don't want to keep sending the same records to the server
				// if they didn't change. Also if we do it causes a blowup
				this.editedById = {}
				this.trigger('done', records)
			},
			error: xhr => {
				// this manual banner-error stuff doesn't seem like it should
				// be necessary, but I'm in a rush, so I don't have time to track
				// this down right now (#52142)
				this.$('.banner-error')
					.removeClass('is-not-displayed')
					.closest('form')
						.addClass('has-error')

				this.formView.error(xhr)
			}
		})
		return xhr
	},

	updateRecordListItemsById: function(records, addKey) {
		let response = this.options.widget.recordListResponse
		_.each(records, record => {
			// if we stripped the generatedKey before sending to the API, add it
			// back in
			if (addKey) {
				record.record?.push(record.generatedKey)
				record.edited?.push(false)
			}

			let id = record.record[this.pkIndex]
			this.recordListItemsById[id] = record

			// there are cases where the form is not refetched but we rerender
			// the record list, so update the record list response as well
			const responseIndex = _.findIndex(response.records, r => r[this.pkIndex] == id)
			response.records[responseIndex] = record.record
			response.edited[responseIndex] = record.edited
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeComponents()
	},

	removeComponents: function() {
		_.invokeMap([this.recordList, this.fieldValueFormatter,
			this.finder, this.noNewRecordsTooltip, this.notEditablePkTooltip], 'remove')
		_.invoke(this.covertButtonTooltips, 'cleanup')
		_.invokeMap(_.flatten(_.values(this.fieldValueControlsById)), 'remove')
	}

})

export default RecordListWidget

RecordListWidget.__filename__ = 'forms/widgets/record-list-widget.js'
